import { useState, useEffect } from 'react'
import { History, ChevronDown, ChevronUp } from 'lucide-react'
import { dealsApi } from '@api/client'
import type { AuditLogEntry } from '@api/types'

interface ChangeHistoryProps {
  dealId: number
  table?: string // 'quotes' | 'scopes' | 'roi_calculations'
}

const ACTION_LABELS: Record<string, string> = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
  finalize: 'Finalised',
}

function timeAgo(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr + (dateStr.endsWith('Z') ? '' : 'Z')).getTime()
  const seconds = Math.floor((now - then) / 1000)

  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  return `${months}mo ago`
}

export function ChangeHistory({ dealId, table }: ChangeHistoryProps) {
  const [entries, setEntries] = useState<AuditLogEntry[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!isOpen || !dealId) return

    const load = async () => {
      setIsLoading(true)
      try {
        const res = await dealsApi.getAuditLog(dealId, table)
        if (res.success && res.data) {
          setEntries(res.data)
        }
      } catch (e) {
        console.error('Failed to load change history:', e)
      } finally {
        setIsLoading(false)
      }
    }

    load()
  }, [isOpen, dealId, table])

  return (
    <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] rounded-lg transition-colors"
      >
        <span className="flex items-center gap-2">
          <History size={16} className="text-[var(--text-muted)]" />
          Change History
        </span>
        {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {isOpen && (
        <div className="px-4 pb-3 border-t border-[var(--border-color)]">
          {isLoading ? (
            <p className="text-xs text-[var(--text-muted)] py-3">Loading...</p>
          ) : entries.length === 0 ? (
            <p className="text-xs text-[var(--text-muted)] py-3">No changes recorded yet</p>
          ) : (
            <div className="divide-y divide-[var(--border-color)]">
              {entries.map((entry) => (
                <div key={entry.id} className="py-2.5 first:pt-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-[var(--text-primary)]">
                      {ACTION_LABELS[entry.action] || entry.action}
                    </span>
                    <span className="text-xs text-[var(--text-muted)]">
                      {timeAgo(entry.created_at)}
                    </span>
                  </div>
                  {entry.user_name && (
                    <p className="text-xs text-[var(--text-muted)] mt-0.5">
                      by {entry.user_name}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
